import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalTypeaheadMenuPlugin } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { $createTextNode, DecoratorNode } from 'lexical';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import UserMenuChip from '../components/UserMenuChip';
import { fetchMentionableServiceUsers } from '../redux/slices/users';

const MentionMenu = styled('div')(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    position: 'absolute',
    zIndex: theme.zIndex.modal + 1,
}));

const MentionList = styled('ul')({
    listStyle: 'none',
    margin: 0,
    padding: '5px 0',
    maxHeight: '200px',
    overflowY: 'auto',
});

const MentionItem = styled('li')(({ theme, isSelected }) => ({
    padding: '5px 10px',
    cursor: 'pointer',
    wordBreak: 'keep-all',
    whiteSpace: 'nowrap',
    color: theme.palette.text.primary,
    backgroundColor: isSelected ? theme.palette.action.selected : 'transparent',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

function MentionTypeaheadMenuItem({ index, isSelected, onClick, onMouseEnter, option }) {

    return (
        <MentionItem
            key={option.key}
            tabIndex={-1}
            isSelected={isSelected}
            ref={option.setRefElement}
            role="option"
            aria-selected={isSelected}
            id={`typeahead-item-${index}`}
            onMouseEnter={onMouseEnter}
            onClick={onClick}>
            {option.name}
        </MentionItem>
    );
}

function MentionChip({ mention }) {
    const fresh = useSelector((state) => {
        const id = String(mention?._id);
        const users = state.accountUsers?.users || [];
        const serviceUsers = state.accountUsers?.serviceUsers || [];
        return users.find((u) => String(u._id) === id) || serviceUsers.find((u) => String(u._id) === id);
    });
    return <UserMenuChip user={fresh || mention} menuItems={{ reAssign: false }} />;
}

export class MentionNode extends DecoratorNode {
    __mention;

    static getType() {
        return 'mention';
    }

    static clone(node) {
        return new MentionNode(node.__mention, node.__key);
    }

    constructor(mention, key) {
        super(key);
        this.__mention = mention;
    }

    createDOM() {
        const span = document.createElement('span');
        span.style.display = 'inline-block';
        span.style.verticalAlign = 'middle';
        return span;
    }

    updateDOM() {
        return false;
    }

    isInline() {
        return true;
    }

    static importJSON(serializedNode) {
        return $createMentionNode(serializedNode.mention);
    }

    exportJSON() {
        return {
            mention: this.__mention,
            type: 'mention',
            version: 1,
        };
    }

    decorate() {
        return <MentionChip mention={this.__mention} menuItems={{ reAssign: false }} />;
    }
}

export function $createMentionNode(mention) {
    return new MentionNode(mention);
}

export default function MentionsPlugin() {
    const [editor] = useLexicalComposerContext();
    const dispatch = useDispatch();
    const [queryString, setQueryString] = useState(null);

    const users = useSelector(state => state.accountUsers.users);
    const serviceUsers = useSelector(state => state.accountUsers.serviceUsers);
    const serviceUsersStatus = useSelector(state => state.accountUsers.serviceUsersStatus);

    useEffect(() => {
        if (serviceUsersStatus === 'idle') {
            dispatch(fetchMentionableServiceUsers());
        }
    }, [dispatch, serviceUsersStatus]);

    const checkForMentionMatch = useCallback(
        (text) => {
            const mentionMatch = /@(\w+)$/.exec(text);
            if (mentionMatch) {
                return {
                    leadOffset: mentionMatch.index,
                    matchingString: mentionMatch[1],
                    replaceableString: mentionMatch[0],
                };
            }
            return null;
        },
        []
    );

    const options = useMemo(() => {
        if (!queryString) {
            return [];
        }
        const seen = new Set();
        const combined = [];
        [...(users || []), ...(serviceUsers || [])].forEach((user) => {
            if (!user || !user._id) return;
            const id = String(user._id);
            if (seen.has(id)) return;
            seen.add(id);
            combined.push(user);
        });
        return combined
            .filter((user) => user.fullName && user.fullName.toLowerCase().includes(queryString.toLowerCase()))
            .map((user) => ({
                name: user.fullName,
                user: user,
                setRefElement: () => {},
            }));
    }, [users, serviceUsers, queryString]);

    const onSelectOption = useCallback(
        (selectedOption, nodeToReplace, closeMenu) => {
            editor.update(() => {
                const mentionNode = $createMentionNode(selectedOption.user);
                if (nodeToReplace) {
                    nodeToReplace.replace(mentionNode);
                }
                const textNode = $createTextNode(' ');
                mentionNode.insertAfter(textNode);
                textNode.select();
                closeMenu();
            });
        },
        [editor]
    );

    return (
        <LexicalTypeaheadMenuPlugin
            onQueryChange={setQueryString}
            onSelectOption={onSelectOption}
            triggerFn={checkForMentionMatch}
            options={options}
            menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) =>
                anchorElementRef.current && options.length
                    ? ReactDOM.createPortal(
                        <MentionMenu>
                            <MentionList>
                                {options.map((option, i) => (
                                    <MentionTypeaheadMenuItem
                                        key={option.user._id}
                                        index={i}
                                        isSelected={selectedIndex === i}
                                        onClick={() => {
                                            setHighlightedIndex(i);
                                            selectOptionAndCleanUp(option);
                                        }}
                                        onMouseEnter={() => {
                                            setHighlightedIndex(i);
                                        }}

                                        option={option}
                                    />
                                ))}
                            </MentionList>
                        </MentionMenu>,
                        anchorElementRef.current
                    )
                    : null
            }
        />
    );
}